import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { formatPrice } from "../lib/catalog";
import { paymentRequest } from "../lib/payments";
import { AuthModal } from "./AuthModal";

type Pix = {
  orderId: string; amountCents: number; status: string; copyPaste: string | null;
  qrCodeUrl: string | null; expiresAt: number | null; instructionsUrl: string | null; testMode: boolean;
};
function secureUrl(value: string | null) {
  try { const url = new URL(value || ""); return url.protocol === "https:" ? url.href : undefined; } catch { return undefined; }
}

export function PaymentDialog({ doramaId, price = 27, onClose }: {
  doramaId?: string; price?: number | null; onClose: () => void;
}) {
  const { user, loading } = useAuth();
  const [mode, setMode] = useState<"choice" | "pix-form" | "pix">("choice");
  const [authOpen, setAuthOpen] = useState(false);
  const [fullName, setFullName] = useState(String(user?.user_metadata.full_name || user?.user_metadata.display_name || ""));
  const [taxId, setTaxId] = useState("");
  const [needsTaxId, setNeedsTaxId] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyNotice, setCopyNotice] = useState("");
  const [pix, setPix] = useState<Pix | null>(null);
  const [checking, setChecking] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [pollAttempt, setPollAttempt] = useState(0);
  const orderRef = useRef<string | null>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const inFlight = useRef(false);
  const titleId = useId();
  const storageKey = `doraflix-pix:${user?.id}:${doramaId || "lifetime"}`;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.stopImmediatePropagation(); onClose(); }
      if (event.key === "Tab" && !authOpen) {
        const elements = dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input, textarea, a[href]');
        if (!elements?.length) return;
        const first = elements[0]; const last = elements[elements.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) { event.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener("keydown", keydown, true);
    return () => { document.body.style.overflow = previousOverflow; previousFocus?.focus(); window.removeEventListener("keydown", keydown, true); };
  }, [onClose, authOpen]);
  useEffect(() => {
    if (!user) return;
    setAuthOpen(false);
    setFullName(String(user.user_metadata.full_name || user.user_metadata.display_name || ""));
  }, [user]);
  useEffect(() => {
    setPix(null); setMode("choice"); orderRef.current = null; setError(null);
  }, [user?.id]);
  const orderId = pix?.orderId;
  const status = pix?.status;
  useEffect(() => {
    if (!user || !orderId || mode !== "pix" || status !== "pending") return;
    let active = true; let timer: number | undefined; let checks = 0;
    setChecking(true);
    async function check() {
      try {
        const result = await paymentRequest<Omit<Pix, "orderId" | "amountCents">>("stripe-pix", { action: "status", orderId });
        if (!active) return;
        setPix((previous) => previous ? { ...previous, ...result } : null);
        setError(null);
        if (result.status === "pending" && ++checks < 120) timer = window.setTimeout(check, 5000);
        else setChecking(false);
      } catch (error) {
        if (!active) return;
        setError(error instanceof Error ? error.message : "Não foi possível confirmar o Pix.");
        setChecking(false);
      }
    }
    void check();
    return () => { active = false; window.clearTimeout(timer); };
  }, [user, orderId, mode, status, pollAttempt]);
  useEffect(() => {
    if (mode !== "pix" || status !== "pending") return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [mode, status]);

  async function card() {
    if (!user) { setAuthOpen(true); return; }
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError(null);
    try {
      const { url } = await paymentRequest<{ url: string }>("stripe-checkout", { kind: doramaId ? "dorama" : "lifetime", doramaId });
      const target = new URL(url);
      if (target.protocol !== "https:" || target.hostname !== "checkout.stripe.com") throw new Error("Endereço de pagamento inválido.");
      window.location.assign(target.href);
    } catch (error) { setError(error instanceof Error ? error.message : "Não foi possível abrir o pagamento."); setBusy(false); inFlight.current = false; }
  }
  async function generatePix(event?: FormEvent) {
    event?.preventDefault();
    if (!user) { setAuthOpen(true); return; }
    if (inFlight.current) return;
    setMode("pix-form"); setError(null);
    if (fullName.trim().length < 3) return;
    inFlight.current = true; setBusy(true);
    try {
      if (!orderRef.current) {
        try { orderRef.current = sessionStorage.getItem(storageKey); } catch { /* Storage may be disabled. */ }
        orderRef.current ||= crypto.randomUUID();
        try { sessionStorage.setItem(storageKey, orderRef.current); } catch { /* Continue without persistence. */ }
      }
      const result = await paymentRequest<Pix>("stripe-pix", {
        action: "create", orderId: orderRef.current, kind: doramaId ? "dorama" : "lifetime", doramaId, fullName, taxId,
      });
      setPix(result); setMode("pix"); setCopyNotice(""); setNow(Date.now());
    } catch (error) {
      const message = error instanceof Error ? error.message : "Não foi possível gerar o Pix.";
      setNeedsTaxId(/CPF|CNPJ|tax_id/i.test(message));
      setError(message);
    }
    finally { setBusy(false); inFlight.current = false; }
  }
  async function copy() {
    try { await navigator.clipboard.writeText(pix?.copyPaste || ""); setCopyNotice("Código copiado! Cole no aplicativo do seu banco."); }
    catch { setCopyNotice("Selecione o código abaixo e copie manualmente."); }
  }
  function newCode() {
    orderRef.current = null;
    try { sessionStorage.removeItem(storageKey); } catch { /* No persistence. */ }
    setPix(null); setMode("pix-form"); setError(null);
  }
  const remaining = pix?.expiresAt ? Math.max(0, Math.ceil((pix.expiresAt * 1000 - now) / 1000)) : null;
  const expired = pix?.status === "expired" || (pix?.status === "pending" && remaining === 0);
  return createPortal(<div className="payment-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <section ref={dialogRef} tabIndex={-1} className="payment-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="payment-handle" aria-hidden="true" />
      <button className="payment-close" onClick={onClose} aria-label="Fechar pagamento">×</button>
      <h2 id={titleId}>{mode === "choice" ? "Escolha como pagar" : busy ? "Gerando PIX…" : pix?.status === "paid" ? "Pagamento confirmado!" : mode === "pix" ? "Escaneie o QR Code" : "Pague com Pix"}</h2>
      <p className="payment-value">Valor: <strong>{formatPrice(pix ? pix.amountCents / 100 : price)}</strong></p>
      {mode === "choice" && <div className="payment-methods">
        <button className="payment-method" onClick={() => void generatePix()} disabled={loading || busy}>
          <span className="payment-method-icon pix-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="m12 3 9 9-9 9-9-9 9-9Z"/><path d="m7 8 5 5 5-5M7 16l5-5 5 5"/></svg></span>
          <span><strong>PIX</strong><small>Liberação após confirmação</small></span><b>Recomendado</b>
        </button>
        <button className="payment-method" onClick={card} disabled={loading || busy}>
          <span className="payment-method-icon card-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="2"/><path d="M3 10h18"/></svg></span>
          <span><strong>Cartão de crédito</strong><small>{busy ? "Abrindo pagamento…" : "Pague no checkout seguro da Stripe"}</small></span>
        </button>
      </div>}
      {mode === "pix-form" && busy && <div className="payment-generating" role="status"><span className="payment-spinner" aria-hidden="true" /><p>Preparando seu pagamento…</p></div>}
      {mode === "pix-form" && !busy && <form className="payment-pix-form" onSubmit={generatePix}>
        <p>Confirme seus dados para gerar o QR Code.</p>
        <label>Nome completo<input value={fullName} onChange={(event) => setFullName(event.target.value)} autoComplete="name" required minLength={3} maxLength={150} disabled={busy} /></label>
        {needsTaxId && <label>CPF ou CNPJ<input value={taxId} onChange={(event) => setTaxId(event.target.value)} inputMode="numeric" maxLength={18} autoComplete="off" required disabled={busy} /></label>}
        <button className="payment-action" disabled={busy}>{busy ? "Gerando Pix…" : "Gerar QR Code e copia e cola"}</button>
      </form>}
      {mode === "pix" && pix && <div className="payment-pix-result">
        {pix.status === "paid" ? <><div className="payment-success" aria-hidden="true">✓</div><p role="status">Seu acesso já está liberado.</p><Link className="payment-action" to="/minhas-compras" onClick={onClose}>Assistir agora</Link></> : pix.status === "refunded" ? <p role="status">Este pagamento foi reembolsado.</p> : <>
          {pix.testMode && <p className="payment-test-note">Ambiente de teste. Este Pix não deve ser pago pelo banco.</p>}
          {expired ? <p role="status">Este código expirou. Se você já pagou, verifique a confirmação antes de gerar outro.</p> : <>
            {secureUrl(pix.qrCodeUrl) && <img className="payment-qr" src={secureUrl(pix.qrCodeUrl)} alt="QR Code para pagar com Pix" />}
            <p>Escaneie com o aplicativo do banco ou use o copia e cola.</p>
            {remaining !== null && <small>Válido por {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")}</small>}
            {pix.copyPaste && <><button className="payment-action" onClick={copy}>Copiar código Pix</button><label className="payment-copy-label">Pix copia e cola<textarea readOnly value={pix.copyPaste} onFocus={(event) => event.target.select()} /></label></>}
            {secureUrl(pix.instructionsUrl) && <a className="payment-instructions" href={secureUrl(pix.instructionsUrl)} target="_blank" rel="noreferrer">{pix.testMode ? "Abrir simulação de pagamento" : "Abrir instruções do Pix"}</a>}
          </>}
          <p role="status">{checking ? "Aguardando confirmação do pagamento…" : "Verifique o pagamento quando concluir no banco."}</p>
          <button className="payment-secondary" onClick={() => setPollAttempt((value) => value + 1)}>Verificar pagamento</button>
          {expired && <button className="payment-secondary" onClick={newCode}>Gerar outro Pix</button>}
        </>}
        {copyNotice && <p role="status">{copyNotice}</p>}
      </div>}
      {error && <p className="payment-error" role="alert">{error}</p>}
      {mode !== "choice" && pix?.status !== "paid" && <button className="payment-back" disabled={busy} onClick={() => { setMode("choice"); setError(null); }}>← Outras formas de pagamento</button>}
      {!user && <p className="payment-login-note">Entre ou crie sua conta para vincular a compra.</p>}
      <p className="payment-secure">🔒 Pagamento seguro pela Stripe</p>
    </section>
    <div className="payment-auth"><AuthModal open={authOpen && !user} onClose={() => setAuthOpen(false)} /></div>
  </div>, document.body);
}
